const API_BASE = import.meta.env.PROD 
  ? 'https://pickupstix-gateway.unemployed.workers.dev'
  : '/api' // Proxied in dev

class ApiClient {
  private token: string | null = null

  constructor() {
    this.token = localStorage.getItem('auth_token')
  }

  setToken(token: string) {
    this.token = token
    localStorage.setItem('auth_token', token)
  }

  clearToken() {
    this.token = null
    localStorage.removeItem('auth_token')
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options.headers,
      }

      if (this.token) {
        headers.Authorization = `Bearer ${this.token}`
      }

      const response = await fetch(url, {
        ...options,
        headers,
      })

      if (!response.ok) {
        const errorText = await response.text()
        let error
        try {
          error = JSON.parse(errorText)
        } catch {
          error = { message: errorText || 'Unknown error' }
        }
        throw new Error(error.description || error.message || `Request failed with status ${response.status}`)
      }

      return response.json()
    } catch (error) {
      console.error('API request failed:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Network request failed')
    }
  }

  // Auth endpoints
  async login(credentials: { username: string; password: string }) {
    return this.request<{ user: any; token: { token: string } }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async validateToken() {
    return this.request<{ user: any }>('/auth/validate', {
      method: 'POST',
    })
  }

  // TAXII endpoints
  async getDiscovery() {
    return this.request('/taxii2/')
  }

  async getCollections(includeStats = false) {
    const params = includeStats ? '?stats=true' : '';
    return this.request<{ collections: any[] }>(`/api/v1/collections/${params}`)
  }

  async getCollection(id: string) {
    return this.request(`/api/v1/collections/${id}/`)
  }

  async getObjects(collectionId: string, params?: { limit?: number; added_after?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.added_after) searchParams.set('added_after', params.added_after)
    
    const query = searchParams.toString()
    const url = `/api/v1/collections/${collectionId}/objects/${query ? `?${query}` : ''}`
    
    return this.request<{ objects: any[]; more?: boolean }>(url)
  }

  async addObjects(collectionId: string, objects: any[]) {
    return this.request(`/api/v1/collections/${collectionId}/objects/`, {
      method: 'POST',
      body: JSON.stringify({ objects }),
    })
  }

  async getManifest(collectionId: string) {
    return this.request<{ objects: any[] }>(`/api/v1/collections/${collectionId}/manifest/`)
  }

  // Feed management
  async getFeeds() {
    return this.request<{ feeds: any[] }>('/admin/feeds/')
  }

  async createFeed(feedData: any) {
    return this.request('/admin/feeds/', {
      method: 'POST',
      body: JSON.stringify(feedData),
    })
  }

  async updateFeed(feedId: string, feedData: any) {
    return this.request(`/admin/feeds/${feedId}`, {
      method: 'PUT',
      body: JSON.stringify(feedData),
    })
  }

  async deleteFeed(feedId: string, deleteObjects?: boolean) {
    const url = deleteObjects 
      ? `/admin/feeds/${feedId}?deleteObjects=true` 
      : `/admin/feeds/${feedId}`;
    
    return this.request(url, {
      method: 'DELETE',
    })
  }

  async triggerFeedPoll(feedId: string) {
    return this.request(`/admin/feeds/${feedId}/poll`, {
      method: 'POST',
    })
  }

  async validateFeed(feedData: { url: string; auth_type?: string; auth_config?: any }) {
    return this.request('/admin/feeds/validate', {
      method: 'POST',
      body: JSON.stringify(feedData),
    })
  }

  async discoverCollections(feedData: { url: string; auth_type?: string; auth_config?: any }) {
    return this.request('/admin/feeds/discover-collections', {
      method: 'POST',
      body: JSON.stringify(feedData),
    })
  }

  // Collection management
  async createCollection(collectionData: any) {
    return this.request('/admin/collections/', {
      method: 'POST',
      body: JSON.stringify(collectionData),
    })
  }

  async updateCollection(collectionId: string, collectionData: any) {
    return this.request(`/admin/collections/${collectionId}`, {
      method: 'PUT',
      body: JSON.stringify(collectionData),
    })
  }

  async deleteCollection(collectionId: string) {
    return this.request(`/admin/collections/${collectionId}`, {
      method: 'DELETE',
    })
  }

  // User management
  async getUsers() {
    return this.request<{ users: any[] }>('/admin/users/')
  }

  async createUser(userData: any) {
    return this.request('/admin/users/', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async updateUser(userId: string, userData: any) {
    return this.request(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    })
  }

  async deleteUser(userId: string) {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE',
    })
  }

  // API key management
  async getApiKeys() {
    return this.request<{ keys: any[] }>('/admin/api-keys/')
  }

  async createApiKey(keyData: any) {
    return this.request('/admin/api-keys/', {
      method: 'POST',
      body: JSON.stringify(keyData),
    })
  }

  async deleteApiKey(keyId: string) {
    return this.request(`/admin/api-keys/${keyId}`, {
      method: 'DELETE',
    })
  }

  // Search functionality
  async searchObjects(query: string, options?: { collection_id?: string; limit?: number }) {
    const searchParams = new URLSearchParams({ q: query })
    if (options?.collection_id) searchParams.set('collection_id', options.collection_id)
    if (options?.limit) searchParams.set('limit', options.limit.toString())
    
    return this.request<{ results: any[]; total: number }>(`/admin/search/?${searchParams}`)
  }

  // Platform statistics
  async getStats() {
    return this.request<{ stats: any }>('/admin/stats/')
  }

  // Health check
  async getHealth() {
    return this.request('/health')
  }
}

export const api = new ApiClient()